"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';

const InfraestruturaRedePage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };

  // Categorias de dispositivos e serviços
  const tiposRede = [
    {
      id: 'cabeamento',
      title: 'Cabeamento Estruturado',
      description: 'Sistemas de cabeamento organizados e eficientes',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
          <line x1="2" y1="10" x2="22" y2="10"></line>
        </svg>
      ),
      solucoes: [
        'Cabeamento Estruturado CAT 5e, CAT 6 e CAT 6a',
        'Implantação de Backbone de Fibra Óptica',
        'Certificação de cabeamento com equipamentos especializados',
        'Organização de Data Centers e racks',
        'Identificação e documentação de rede'
      ]
    },
    {
      id: 'redes',
      title: 'Conectividade e Redes',
      description: 'Soluções avançadas para redes de alta performance',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
          <line x1="6" y1="6" x2="6.01" y2="6"></line>
          <line x1="6" y1="18" x2="6.01" y2="18"></line>
        </svg>
      ),
      solucoes: [
        'Switches gerenciáveis corporativos (Layer 2/Layer 3)',
        'Roteadores de alto desempenho',
        'Redes Wi-Fi corporativas gerenciadas',
        'Redes definidas por software (SDN)',
        'Balanceamento de cargas e redundância'
      ]
    },
    {
      id: 'seguranca',
      title: 'Segurança de Rede',
      description: 'Proteção abrangente para sua infraestrutura',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      solucoes: [
        'Firewalls de próxima geração (NGFW)',
        'VPNs para acesso remoto seguro',
        'Sistemas de detecção e prevenção de intrusões (IDS/IPS)',
        'Segmentação de rede (VLANs)',
        'Controle de acesso à rede (NAC)'
      ]
    },
    {
      id: 'servidores',
      title: 'Servidores e Storage',
      description: 'Soluções de armazenamento e processamento corporativo',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
          <rect x="9" y="9" width="6" height="6"></rect>
          <line x1="9" y1="1" x2="9" y2="4"></line>
          <line x1="15" y1="1" x2="15" y2="4"></line>
          <line x1="9" y1="20" x2="9" y2="23"></line>
          <line x1="15" y1="20" x2="15" y2="23"></line>
          <line x1="20" y1="9" x2="23" y2="9"></line>
          <line x1="20" y1="14" x2="23" y2="14"></line>
          <line x1="1" y1="9" x2="4" y2="9"></line>
          <line x1="1" y1="14" x2="4" y2="14"></line>
        </svg>
      ),
      solucoes: [
        'Virtualização (VMware, Hyper-V)',
        'Storage NAS e SAN corporativo',
        'Soluções de backup e recuperação de desastres',
        'Servidores físicos e cloud computing',
        'Sistemas de alta disponibilidade (cluster)'
      ]
    }
  ];

  // Serviços principais
  const servicosPrincipais = [
    {
      titulo: 'Projeto de Rede Completo',
      descricao: 'Desenvolvimento de projetos de rede corporativa seguindo as melhores práticas e padrões internacionais',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
          <polyline points="2 17 12 22 22 17"></polyline>
          <polyline points="2 12 12 17 22 12"></polyline>
        </svg>
      )
    },
    {
      titulo: 'Cabeamento Estruturado',
      descricao: 'Instalação de cabeamento de rede organizado seguindo padrões ANSI/TIA/EIA com certificação',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14"></path>
          <path d="M12 5l7 7-7 7"></path>
        </svg>
      )
    },
    {
      titulo: 'Wi-Fi Corporativo',
      descricao: 'Implementação de redes sem fio corporativas gerenciadas com cobertura garantida',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
          <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
          <line x1="12" y1="20" x2="12.01" y2="20"></line>
        </svg>
      )
    },
    {
      titulo: 'Firewall e Segurança',
      descricao: 'Implementação de soluções de proteção contra ameaças cibernéticas e gestão de segurança',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      destaque: true
    },
    {
      titulo: 'Servidores e Virtualização',
      descricao: 'Implantação de servidores físicos e ambientes virtualizados para aplicações corporativas',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
          <line x1="6" y1="6" x2="6.01" y2="6"></line>
          <line x1="6" y1="18" x2="6.01" y2="18"></line>
        </svg>
      )
    },
    {
      titulo: 'Cloud Computing',
      descricao: 'Migração e gestão de infraestrutura em ambientes de nuvem pública, privada ou híbrida',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
        </svg>
      )
    },
    {
      titulo: 'Monitoramento de Rede',
      descricao: 'Implementação de sistemas de monitoramento 24/7 para garantir disponibilidade e performance',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      )
    },
    {
      titulo: 'Consultoria e Suporte',
      descricao: 'Serviços de consultoria especializada e suporte técnico para infraestrutura de redes',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
      )
    }
  ];

  // Problemas comuns
  const problemasComuns = [
    {
      titulo: 'Rede instável ou lenta',
      sintomas: 'Conexões caindo frequentemente, lentidão no acesso a sistemas, transferências de arquivo demoradas',
      solucao: 'Diagnóstico completo da infraestrutura, identificação de gargalos, substituição de equipamentos obsoletos, reorganização da topologia de rede'
    },
    {
      titulo: 'Falhas de segurança',
      sintomas: 'Acesso não autorizado, vulnerabilidades de rede, falta de políticas de segurança adequadas',
      solucao: 'Implementação de firewalls de próxima geração, segmentação de rede (VLANs), criação de políticas de segurança robustas, sistemas de detecção de intrusão'
    },
    {
      titulo: 'Problemas de conectividade Wi-Fi',
      sintomas: 'Áreas sem cobertura, pontos de acesso sobrecarregados, interferência entre dispositivos',
      solucao: 'Site survey completo, reposicionamento estratégico de access points, implementação de controladores WLAN, configuração de canais adequados'
    },
    {
      titulo: 'Infraestrutura desorganizada',
      sintomas: 'Cabeamento desorganizado, falta de documentação, dificuldade em identificar conexões',
      solucao: 'Reorganização do cabeamento estruturado seguindo normas ANSI/TIA/EIA, documentação completa da rede, etiquetagem padronizada'
    },
    {
      titulo: 'Falta de redundância',
      sintomas: 'Interrupções de serviço em caso de falhas, pontos únicos de falha na infraestrutura',
      solucao: 'Implementação de links redundantes, soluções de alta disponibilidade, balanceadores de carga, sistemas de backup e recuperação'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      pergunta: 'Qual o prazo médio para implementação de um projeto de rede corporativa?',
      resposta: 'O prazo para implementação de um projeto de rede corporativa varia conforme a complexidade e tamanho da infraestrutura. Para pequenas empresas, um projeto completo pode levar de 1 a 2 semanas. Para empresas de médio porte, o prazo médio é de 3 a 6 semanas. Já para grandes corporações com múltiplos andares ou unidades, o processo pode levar de 2 a 4 meses. Estes prazos incluem as fases de análise, planejamento, implementação e testes. Projetos que envolvem obras civis para passagem de cabeamento podem ter prazos estendidos.'
    },
    {
      pergunta: 'Como garantir a segurança da rede corporativa?',
      resposta: 'A segurança de rede corporativa é implementada em camadas, seguindo o princípio de defesa em profundidade. Utilizamos uma combinação de soluções que incluem: firewalls de próxima geração (NGFW) para controle de tráfego; sistemas de detecção e prevenção de intrusões (IDS/IPS); segmentação de rede através de VLANs; controle de acesso baseado em identidade; criptografia para dados em trânsito; sistemas avançados de proteção contra malware; e políticas de segurança bem definidas. Além disso, realizamos auditorias periódicas de segurança e testes de penetração para identificar e corrigir vulnerabilidades. Aliado à tecnologia, também enfatizamos a importância de treinamentos de conscientização para colaboradores, pois o fator humano é frequentemente o elo mais vulnerável da cadeia de segurança.'
    },
    {
      pergunta: 'Quais benefícios de uma rede Wi-Fi corporativa em comparação com soluções convencionais?',
      resposta: 'Uma rede Wi-Fi corporativa oferece vantagens significativas sobre soluções convencionais de consumo. Primeiramente, oferece maior cobertura e capacidade, com access points profissionais que suportam mais conexões simultâneas e tecnologias como MU-MIMO. A segurança é substancialmente superior, com recursos como autenticação 802.1x, isolamento de clientes e detecção de redes não autorizadas. O gerenciamento centralizado permite configurar e monitorar todos os access points de forma unificada, facilitando atualizações e resolução de problemas. A qualidade de serviço (QoS) permite priorizar tráfego crítico de negócios, como videoconferências e VoIP. Além disso, redes Wi-Fi corporativas oferecem melhor escalabilidade para crescimento futuro e análises detalhadas de uso e desempenho, permitindo otimização contínua baseada em dados reais.'
    },
    {
      pergunta: 'É possível integrar nossa infraestrutura local com serviços em nuvem?',
      resposta: 'Sim, a integração entre infraestrutura local (on-premises) e serviços em nuvem é não apenas possível, mas frequentemente recomendada para empresas modernas, criando um ambiente híbrido. Implementamos soluções que permitem conexões seguras entre sua infraestrutura local e provedores de nuvem pública (AWS, Azure, Google Cloud) através de conexões dedicadas ou VPN site-to-site. Utilizamos tecnologias de virtualização e containerização que facilitam a portabilidade de aplicações entre ambientes. Serviços de diretório como Active Directory podem ser estendidos para a nuvem, mantendo políticas de segurança consistentes. Sistemas de gerenciamento unificado possibilitam administrar recursos locais e na nuvem através de uma única interface. Esta abordagem híbrida permite que sua empresa aproveite a escalabilidade e inovação da nuvem, mantendo sistemas críticos ou regulados localmente quando necessário.'
    },
    {
      pergunta: 'Como é feito o dimensionamento de uma rede para atender ao crescimento da empresa?',
      resposta: 'O dimensionamento adequado de uma rede corporativa para suportar crescimento futuro envolve uma abordagem estratégica em múltiplas camadas. Inicialmente, realizamos uma análise detalhada das necessidades atuais e projeções de crescimento de 3 a 5 anos. A arquitetura é projetada com capacidade excedente planejada, utilizando equipamentos que suportam expansão (switches empilháveis, roteadores modulares). Implementamos esquemas de endereçamento IP com espaço suficiente para expansão e segmentação futura. Escolhemos tecnologias escaláveis como 10G/40G/100G nos backbones e equipamentos que permitam atualizações de firmware para novas funcionalidades. O cabeamento estruturado é instalado com pontos extras estratégicos e conduits com espaço para expansão. Realizamos também avaliações periódicas de desempenho e capacidade para identificar necessidades de upgrade antes que se tornem críticas. Esta abordagem garante que sua infraestrutura cresça organicamente com seu negócio, minimizando interrupções e reinvestimentos prematuros.'
    }
  ];

  return (
    <>
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <Link 
                  href="/servicos" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Serviços
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Infraestrutura de Rede
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#3B82F6" : "#60A5FA"} 
              fillOpacity="0.2" 
            />
          </svg>
          
          <svg 
            className="absolute left-0 bottom-0 transform -translate-x-1/4 translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.2" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={fadeIn}
              className="lg:w-1/2"
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Redes Corporativas
              </div>
              <h1 className={`text-3xl lg:text-4xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Infraestrutura de <span className="text-blue-600">Rede</span> Corporativa
              </h1>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Soluções completas de conectividade, segurança e infraestrutura para ambientes empresariais
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a 
                  href="#contato" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-lg tracking-wide rounded-lg shadow-lg hover:shadow-xl transition-all text-center"
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: '#ffffff'
                  }}
                >
                  Solicitar Orçamento
                </motion.a>
                <motion.a 
                  href="#servicos" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-8 py-3 ${
                    theme === 'dark' 
                      ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  } font-extrabold text-lg tracking-wide rounded-lg shadow hover:shadow-lg transition-all text-center`}
                >
                  Ver Serviços
                </motion.a>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className={`w-full h-80 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'
                } flex items-center justify-center overflow-hidden`}>
                  <Image
                    src="/images/infraestrutura-rede-illustration.svg"
                    alt="Infraestrutura de Rede"
                    width={500}
                    height={400}
                    className="object-contain max-w-full max-h-full"
                  />
                </div>
                
                {/* Estatística flutuante */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -right-6 p-6 rounded-lg shadow-xl ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                  }`}
                >
                  <p className="text-blue-600 font-bold text-4xl">99.9%</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    de disponibilidade garantida
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Soluções de Rede Section */}
      <section 
        id="solucoes" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Soluções Especializadas
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Infraestrutura de Rede Completa
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Oferecemos soluções personalizadas para cada necessidade da sua empresa
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiposRede.map((tipo) => (
              <motion.div
                key={tipo.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg h-full`}
              >
                <div className={`w-12 h-12 mb-4 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-br from-blue-900 to-blue-800 text-blue-300' 
                    : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                  } flex items-center justify-center`}>
                  {tipo.icon}
                </div>
                
                <h3 className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}>{tipo.title}</h3>
                
                <p className={`mb-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>{tipo.description}</p>
                
                <ul className="space-y-2">
                  {tipo.solucoes.map((solucao, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <svg className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-blue-500' : 'text-blue-600'
                      } mt-0.5 flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm`}>{solucao}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços Section */}
      <section 
        id="servicos" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Nossos Serviços
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Serviços de Infraestrutura de Rede
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Soluções completas desde o projeto até a manutenção de sua rede corporativa
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {servicosPrincipais.map((servico, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${
                  servico.destaque 
                    ? theme === 'dark' 
                      ? 'bg-blue-900/40 border border-blue-800' 
                      : 'bg-blue-50 border border-blue-200'
                    : theme === 'dark' 
                      ? 'bg-gray-800' 
                      : 'bg-white'
                } rounded-xl p-4 shadow-lg h-full`}
              >
                <div className={`w-10 h-10 rounded-full ${
                  theme === 'dark' 
                    ? servico.destaque ? 'bg-blue-800 text-blue-200' : 'bg-gray-700 text-gray-300'
                    : servico.destaque ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  } flex items-center justify-center mb-4`}>
                  {servico.icone}
                </div>
                
                <h3 className={`text-lg font-bold ${
                  theme === 'dark' 
                    ? servico.destaque ? 'text-blue-200' : 'text-white'
                    : servico.destaque ? 'text-blue-800' : 'text-gray-900'
                } mb-2`}>{servico.titulo}</h3>
                
                <p className={`text-sm ${
                  theme === 'dark' 
                    ? servico.destaque ? 'text-blue-100/90' : 'text-gray-400'
                    : servico.destaque ? 'text-blue-900/90' : 'text-gray-600'
                }`}>{servico.descricao}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problemas Section */}
      <section 
        id="problemas" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-blue-300 bg-blue-900/50' 
                : 'text-blue-800 bg-blue-100'
              } rounded-full mb-3`}>
              Soluções
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Problemas Comuns de Rede
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Conheça os desafios mais frequentes e como podemos ajudar a resolvê-los
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {problemasComuns.map((problema, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg h-full`}
              >
                <h3 className={`text-lg font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2 flex items-center`}>
                  <svg className={`w-5 h-5 mr-2 ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-500'
                  }`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {problema.titulo}
                </h3>
                
                <div className="mb-3">
                  <p className={`text-sm font-semibold ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  } mb-1`}>Sintomas:</p>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>{problema.sintomas}</p>
                </div>
                
                <div>
                  <p className={`text-sm font-semibold ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  } mb-1`}>Nossa solução:</p>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>{problema.solucao}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq" 
        className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Perguntas Frequentes
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Dúvidas comuns sobre nossos serviços de infraestrutura de rede.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto"
               itemScope
               itemType="https://schema.org/FAQPage">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`mb-6 rounded-xl ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                } shadow-md overflow-hidden`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-6 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold" itemProp="name">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div 
                    className={`px-6 pb-6 pt-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    itemScope
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="contato"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"} 
              fillOpacity="0.05" 
            />
          </svg>
          
          <svg 
            className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.05" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Fale Conosco
              </div>
              <h2 className={`text-2xl md:text-3xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-3`}>
                Solicite uma Avaliação Gratuita
              </h2>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Nossa equipe está pronta para analisar sua infraestrutura atual e propor 
                as melhores soluções para sua empresa
              </p>
              
              <div className="space-y-4">
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a 
                      href="tel:+551932060706" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      (19) 3206-0706
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-green-300' : 'text-green-700'
                  }`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a 
                      href="https://wa.me/5519994799400" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${
                        theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href="mailto:contato@webimports.com.br" 
                      className={`${
                        theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className={`${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'
              } rounded-xl p-5`}
            >
              <h3 className={`text-xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Agende um diagnóstico gratuito
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default InfraestruturaRedePage;